import React, { useEffect, useState } from 'react';
import { Prompt, useHistory, useLocation } from 'react-router';

import { SwapCartBundleModal, SwapCartBundleState } from 'src/components/SwapCartBundleModal/SwapCartBundleModal';
import { redesignV2RoutePath } from 'src/constants/redesignV2';
import { CartContext } from './CartContext';
import { useCartInternal } from './UseCartInternal';
import { CartURLActionType, getCartDataFromLocalStorage, getCartURLAction } from './Util';
import { AddBundleOptions } from './models';

const initialCartData = getCartDataFromLocalStorage();

export const CartProvider: React.FC = ({ children, ...props }) => {
  const location = useLocation();
  const history = useHistory();
  const value = useCartInternal(initialCartData);
  const [swapCartBundleState, setSwapCartBundleState] = useState<SwapCartBundleState | undefined>();
  const currentBundleId = value.cart.bundle?.id;

  /**
   * try to add a bundle by id
   * @param bundleId the bundle to be added
   * @param needsConfirm if false the bundle will be added without confirmation
   * @param needsRedirect if true the user will be redirected to the /cart page
   * @param solutionTitle if set, the solution title will be place on the /cart page
   * @returns true if the bundle is successfully added into the cart or already exists in the cart, false if there is an existing bundle in the cart
   */
  const tryAddBundleById = (bundleId: string, { needsConfirm, needsRedirect, solutionTitle }: AddBundleOptions = {}) : boolean => {
    if (currentBundleId === bundleId) return true;
    if (currentBundleId && needsConfirm) {
      setSwapCartBundleState({ bundleId, needsRedirect, solutionTitle });
      return false;
    }

    value.addBundleById(bundleId, { solutionTitle });
    if (needsRedirect && location.pathname !== redesignV2RoutePath.CART) {
      history.push(redesignV2RoutePath.CART);
    }
    return true;
  };

  /**
   * Check if the nextLocation has a valid cart action
   * @param nextLocation location where the user is navigating to
   * @returns true if the user navigation can continue, either because there was not actions to execute or the action was successfully executed.
   * returns false if the action execution fails
   */
  const checkIfTheUserCanNavigate = (nextLocation: any) : boolean => {
    const cartAction = getCartURLAction({
      ...nextLocation,
      origin: window.location.origin,
    });

    if (!cartAction) return true;

    const wasActionExcutedSuccessfully = excuteCartAction(cartAction);
    return wasActionExcutedSuccessfully;
  };

  /**
   * execute a given cart action
   * @param cartAction action to execute
   * @returns true if the action is executed successfully
   */
  const excuteCartAction = (cartAction: CartURLActionType) : boolean => {
    if (cartAction.action === 'addBundle') {
      return tryAddBundleById(cartAction.bundleId, { needsConfirm: true, needsRedirect: true });
    }
    return false;
  };

  useEffect(() => {
    if (swapCartBundleState) setSwapCartBundleState(undefined);
  }, [currentBundleId]);

  return (
    <CartContext.Provider
      {...props}
      value={{ ...value, addBundleById: tryAddBundleById }}
    >
      {children}
      <SwapCartBundleModal bundleState={[swapCartBundleState, setSwapCartBundleState]} />
      {/*
        Prompt: Used to excuteCartAction before the user navigate away from a page.
        when: currently it checkIfTheUserCanNavigate if there is a currentBundleId in the cart
        message: true to allow navigation, false to prevent navigation
      */}
      <Prompt
        when={!!currentBundleId}
        message={checkIfTheUserCanNavigate}
      />
    </CartContext.Provider>
  );
};

CartProvider.displayName = 'CartProvider';
